import {
  Component,
  ContentChildren,
  ElementRef,
  Input, OnDestroy,
  OnInit,
  QueryList,
  ViewEncapsulation
} from '@angular/core';
import { interval, Subject } from 'rxjs';
import { animate, AnimationOptions, state, style, transition, trigger } from '@angular/animations';
import { takeUntil } from 'rxjs/operators';

import { SlideDirective } from '../../directives/slide.directive';

@Component({
  selector: 'mcrai-swiper',
  template: `<div
  class="mcrai-swiper__wrapper"
  [style.transform]="defaultWrapperOffset"
  [@slide]="animation"
  (@slide.done)="onSlideEnd()"
>
  <div class="mcrai-swiper__slide mcrai-swiper__slide--duplicate" *ngIf="loop" [style.width.px]="slideWidth">
    <ng-container [ngTemplateOutlet]="slides.last.template"></ng-container>
  </div>
  <div class="mcrai-swiper__slide" *ngFor="let slide of slides" [style.width.px]="slideWidth">
    <ng-container [ngTemplateOutlet]="slide.template"></ng-container>
  </div>
  <div class="mcrai-swiper__slide mcrai-swiper__slide--duplicate" *ngIf="loop" [style.width.px]="slideWidth">
    <ng-container [ngTemplateOutlet]="slides.first.template"></ng-container>
  </div>
</div>

<ng-container *ngIf="slides?.length > 1">
  <button
    class="mcrai-swiper__control mcrai-swiper__control--prev"
    [disabled]="isPrevDisabled()"
    (click)="prev()"
    mcrai-icon-button
  >
    <mcrai-icon class="mcrai-swiper__control-icon">arrow_left</mcrai-icon>
  </button>

  <button
    class="mcrai-swiper__control mcrai-swiper__control--next"
    [disabled]="isNextDisabled()"
    (click)="next()"
    mcrai-icon-button
  >
    <mcrai-icon class="mcrai-swiper__control-icon">arrow_right</mcrai-icon>
  </button>

  <ul class="mcrai-swiper__paginator">
    <li class="mcrai-swiper__paginator-item" *ngFor="let slide of slides; let i = index">
      <button
        (click)="setActive(i)"
        class="mcrai-swiper__bullet"
        [class.mcrai-swiper__bullet--active]="i === activeIndex"
      ></button>
    </li>
  </ul>
</ng-container>
`,
  styles: [`.mcrai-swiper{display:block;overflow:hidden;position:relative}.mcrai-swiper__wrapper{display:flex;width:100%}.mcrai-swiper__slide{flex:0 0 auto;height:100%;display:flex;justify-content:center;align-items:stretch}.mcrai-swiper__slide>*{flex:1}.mcrai-icon-button.mcrai-swiper__control{position:absolute;top:0;bottom:0;margin:auto}.mcrai-swiper__control--prev{left:10px}.mcrai-swiper__control--next{right:10px}.mcrai-swiper__paginator{list-style:none;display:flex;position:absolute;bottom:10px;left:0;right:0;justify-content:center}.mcrai-swiper__paginator-item{display:flex;justify-content:center;align-items:center;margin:1px}.mcrai-swiper__bullet{display:block;width:12px;height:12px;border-radius:50%;border:0;outline:0;cursor:pointer;transition:-webkit-transform .2s;transition:transform .2s;transition:transform .2s,-webkit-transform .2s;-webkit-transform:scale(.7);transform:scale(.7);transition-timing-function:cubic-bezier(.25,.8,.25,1)}.mcrai-swiper__bullet.mcrai-swiper__bullet--active,.mcrai-swiper__bullet:hover{-webkit-transform:scale(1);transform:scale(1)}`],
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.mcrai-swiper]': 'true'
  },
  animations: [
    trigger('slide', [
      state('true', style({ transform: 'translateX({{ offset }}px)' }), { params: { offset: 0 } }),
      state('false', style({ transform: 'translateX({{ offset }}px)' }), { params: { offset: 0 } }),
      transition('* => *', animate('{{ duration }}'),{ params: { duration: '250ms' } }),
    ])
  ]
})
export class SwiperComponent implements OnInit, OnDestroy {
  private _destroy: Subject<null> = new Subject<null>();

  @Input() visible: number = 1;
  @Input() step: number = 1;
  @Input() activeIndex: number = 0;
  @Input() loop: boolean = false;
  @Input() autoplay: boolean = false;
  @Input() autoplayDelay: number = 2000;

  @ContentChildren(SlideDirective) private _slides: QueryList<SlideDirective> = new QueryList<SlideDirective>();
  get slides(): QueryList<SlideDirective> {
    return this._slides;
  }

  private _animation;
  get animation() {
    return this._animation;
  }

  get slideWidth(): number {
    return Math.floor(this._elRef.nativeElement.offsetWidth / this.visible);
  }

  get defaultWrapperOffset(): string {
    const offset = this.loop ? this.slideWidth : 0;

    return `translateX(-${ offset }px)`;
  }

  constructor(
    private _elRef: ElementRef
  ) { }

  ngOnInit() { }

  ngAfterViewInit() {
    if (this.autoplay) {
      this.loop = true; // ensure that loop mode is enabled when using autoplay function

      interval(this.autoplayDelay)
        .pipe(
          takeUntil(this._destroy)
        )
        .subscribe(() => this.next());
    }
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  private _moveWrapper(duration: string = '250ms'): void {
    const value = this._animation ? !this._animation.value : true;
    const offset = this.loop ? - this.slideWidth * (this.activeIndex + 1) : - this.slideWidth * this.activeIndex;

    this._animation = {
      value: value,
      params: {
        offset: offset,
        duration: duration
      }
    }
  }

  public prev(): void {
    this.activeIndex -= this.step;
    this._moveWrapper();
  }

  public next(): void {
    this.activeIndex += this.step;
    this._moveWrapper();
  }

  public setActive(index: number): void {
    this.activeIndex = index;
    this._moveWrapper();
  }

  public isPrevDisabled(): boolean {
    return this.loop ? false : this.activeIndex <= 0;
  }

  public isNextDisabled(): boolean {
    return this.loop ? false : this.activeIndex >= this._slides.length - 1;
  }

  public onSlideEnd(): void {
    // we don't need to reset index when loop mode is not enabled
    if (!this.loop) {
      return;
    }

    // reset wrapper to start when end is reached and vice versa
    switch (true) {
      case this.activeIndex >= this._slides.length:
        this.activeIndex = 0;
        this._moveWrapper('0ms');
        return;

      case this.activeIndex < 0:
        this.activeIndex = this._slides.length - 1;
        this._moveWrapper('0ms');
        return;

      default:
        return;
    }
  }

}
